/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Paper } from '@material-ui/core';
import moment from 'moment';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { MENU_WIDTH } from '../constants';
import { getHistoryRecords, getPatientHistoryExport } from '../store/actions/history';
import { getPatient } from '../store/actions/patients';
import FilterPanel from '../components/FilterPanel';

const columns = [
  {
    id: 'alarmTime',
    label: 'Alarm Time',
    minWidth: 170,
    align: 'center',
    format: (value) => moment(value).format('MMM D, h:mm A'),
  },
  {
    id: 'responseTime',
    label: 'Response Time',
    minWidth: 170,
    format: (value) => (value === null ? 'N/A' : moment(value).format('MMM D, h:mm A')),
    align: 'center',
  },
  {
    id: 'response',
    label: 'Response',
    format: (value, row) => {
      if (row.responseTime === null) return 'N/A';
      return value === false ? 'False Alarm' : 'Confirmed';
    },
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'responder',
    label: 'Responder',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'device',
    label: 'Monitor',
    minWidth: 170,
    align: 'center',
  },
];

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
    height: '100%',
  },
  paper: {
    margin: '2em',
  },
  content: {
    flexGrow: 1,
    borderRadius: 4,
  },
  table: {
    width: '100%',
  },
  headerCell: {
    backgroundColor: '#526488',
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'white',
  },
  top: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '2rem 2rem 0 2rem',
  },
}));

const HistoryDetails = () => {
  const dispatch = useDispatch();
  const { id } = useParams(); // Get Patient ID from url params
  const historyRecordsList = useSelector((state) => state.history.historyRecords);
  const historyRecordsCount = useSelector((state) => state.history.historyRecordsCount);
  const currentPatient = useSelector((state) => state.patients.currentPatient);
  const [displayList, setDisplayList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const classes = useStyles();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deviceId, setDeviceId] = useState(sessionStorage.getItem('device'));
  const [idsValue, setIdsValue] = useState(sessionStorage.getItem('ids'));

  useEffect(() => { 
    dispatch(getHistoryRecords(id, idsValue, page + 1, rowsPerPage, startDate, endDate, deviceId ?? ''));
    if (!currentPatient || currentPatient.id.toString() !== id) {
      dispatch(getPatient(id));
    }
  }, [id]);

  useEffect(() => {
    setDisplayList(
      historyRecordsList.map((row) => {
        return {
          alarmTime: row.alert_time,
          responseTime: row.response_time,
          response: row.response,
          responder: row.responder,
          device: row.mac_address,
        };
      }),
    );
  }, [historyRecordsList]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    dispatch(getHistoryRecords(id, idsValue, newPage + 1, rowsPerPage, startDate, endDate, deviceId ?? ''));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    dispatch(getHistoryRecords(id, idsValue, 1, +event.target.value, startDate, endDate, deviceId ?? ''));
  };

  const onFiltersChanged = (filters) => {
    setStartDate(filters.start);
    setEndDate(filters.end);
    setDeviceIdValue(filters.device)
    dispatch(getHistoryRecords(id, idsValue, page + 1, rowsPerPage, filters.start, filters.end, filters.device ?? ''));
  };

  const handleExport = () => {
    getPatientHistoryExport(
      id,
      idsValue,
      startDate,
      endDate,
      currentPatient.id.toString() === id ? currentPatient : null,
      deviceId,
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <h2 className={classes.patient}>
          {currentPatient && currentPatient.id.toString() === id
            ? `Patient: ${currentPatient.first_name} ${currentPatient.last_name}`
            : ''}
        </h2>
        <h2 className={classes.patient}>
          {deviceId
            ? `Monitor: ${deviceId}`
            : ''}
        </h2>
        <FilterPanel onChange={onFiltersChanged} id={parseInt(id, 10)} device={deviceId} handleExport={handleExport} />
      </div>
      <Paper className={classes.paper}>
        <TableContainer className={classes.content}>
          <Table stickyHeader aria-label="sticky table" className={classes.table}>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    className={classes.headerCell}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayList.map((row, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow className={classes.row} hover key={index}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align} className={classes.cell}>
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={historyRecordsCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default HistoryDetails;
